import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Package } from "lucide-react";
import { toast } from "sonner";

interface Product {
  id: string;
  name: string;
}

interface ProductsColumnProps {
  leadId: string;
  products: Product[];
  /**
   * When provided, ProductsColumn skips its own per-lead fetch and uses these IDs.
   * Used by the parent table to batch-load product associations in a single
   * `lead_products IN (...)` query instead of N per-row requests.
   */
  initialSelectedIds?: string[];
}

const ProductsColumn = ({ leadId, products, initialSelectedIds }: ProductsColumnProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds ?? []);
  const [loading, setLoading] = useState(initialSelectedIds === undefined);

  useEffect(() => {
    // Parent provided IDs — keep local state in sync, skip the network call.
    if (initialSelectedIds !== undefined) {
      setSelectedIds(initialSelectedIds);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("lead_products")
        .select("product_id")
        .eq("lead_id", leadId);
      if (cancelled) return;
      setSelectedIds(data?.map((d: any) => d.product_id) || []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, initialSelectedIds]);

  const toggleProduct = async (productId: string, checked: boolean) => {
    try {
      if (checked) {
        const { error } = await supabase.from("lead_products").insert({
          lead_id: leadId,
          product_id: productId,
        });
        if (error) throw error;
        setSelectedIds((prev) => [...prev, productId]);
      } else {
        const { error } = await supabase
          .from("lead_products")
          .delete()
          .eq("lead_id", leadId)
          .eq("product_id", productId);
        if (error) throw error;
        setSelectedIds((prev) => prev.filter((id) => id !== productId));
      }
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const selectedNames = products
    .filter((p) => selectedIds.includes(p.id))
    .map((p) => p.name);

  if (products.length === 0) return <span className="text-muted-foreground text-xs">—</span>;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 text-left text-sm hover:bg-accent/50 rounded px-1 py-0.5 min-w-[80px]">
          {selectedNames.length === 0 ? (
            <span className="text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" />
              None
            </span>
          ) : (
            <div className="flex flex-wrap gap-0.5">
              {selectedNames.slice(0, 2).map((name) => (
                <Badge key={name} variant="secondary" className="text-[10px] px-1 py-0">
                  {name}
                </Badge>
              ))}
              {selectedNames.length > 2 && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                  +{selectedNames.length - 2}
                </Badge>
              )}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="text-xs font-medium text-muted-foreground mb-2">Products</div>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {products.map((product) => (
              <label
                key={product.id}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-accent/50 cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedIds.includes(product.id)}
                  onCheckedChange={(checked) => toggleProduct(product.id, checked as boolean)}
                  className="h-3.5 w-3.5"
                />
                {product.name}
              </label>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ProductsColumn;
